import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
} from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import cn from 'classnames'
import produce from 'immer'

import { propFunc } from 'util/functions'
import { isForwarding } from 'util/draft'
import { selectIsComposingNewTicket } from 'selectors/location'
import { selectRawTicketById } from 'selectors/tickets'

import ToRow from '../ToRow'
import NameRow from '../NameRow'
import PhoneRow from '../PhoneRow'
import CcRow from '../CcRow'
import BccRow from '../BccRow'
import FromRow from '../FromRow'
import SubjectRow from '../SubjectRow'
import Recipients from '../Recipients'
import { styles } from './styles'
import Actions from '../Actions'

const initialState = {
  isEditing: false,
  rowVisibility: {
    from: true,
    to: true,
    name: false,
    phone: false,
    cc: false,
    bcc: false,
    subject: false,
  },
  focusField: null,
  actionsRow: 'to',
}

const actionVisibilityOrder = ['subject', 'bcc', 'cc', 'phone', 'name', 'to']

const getActionsRow = rowVisibility => {
  return actionVisibilityOrder.find(candidate => {
    return rowVisibility[candidate]
  })
}

const buildInitialState = state => {
  return {
    ...initialState,
    ...state,
    actionsRow: getActionsRow(state.rowVisibility) || state.actionsRow,
  }
}

const reducer = produce((draftState, { type, payload = {} }) => {
  switch (type) {
    case 'EXPAND': {
      draftState.isEditing = true
      const { row, shouldFocus = true } = payload
      if (row) {
        draftState.rowVisibility[row] = true
        if (shouldFocus) {
          draftState.focusField = row
        }
      } else if (shouldFocus) {
        draftState.focusField = null
      }
      break
    }
    case 'COLLAPSE': {
      draftState.isEditing = false
      break
    }
    case 'SHOW_ROW': {
      const { row, shouldFocus = true } = payload
      draftState.rowVisibility[row] = true
      if (shouldFocus) {
        draftState.focusField = row
      }
      break
    }
    case 'HIDE_ROW': {
      const { row } = payload
      draftState.rowVisibility[row] = false
      if (draftState.focusField === row) {
        draftState.focusField = null
      }
      break
    }
    default:
    // do nothing
  }

  draftState.actionsRow = getActionsRow(draftState.rowVisibility)
})

const Expanded = ({
  children, // i.e. FormSwitcherButton
  className,
  draft,
  draftId,
  draftType,
  forwarding,
  mailboxId,
  onMailboxClick,
  onSubjectChange,
  onPhoneChange,
  onNameChange,
  onSubjectEnter,
  recipients,
  recipientsTo,
  recipientsCc,
  recipientsBcc,
  recipientsOpenUpward,
  subject,
  phone,
  name,
  ticketId,
  isNote,
}) => {
  const isNewTicket = useSelector(selectIsComposingNewTicket)
  const isForwardingType = isForwarding(draftType) || forwarding
  const toRecipients = recipientsTo || (recipients && recipients.to)
  const ccRecipients = recipientsCc || (recipients && recipients.cc)
  const bccRecipients = recipientsBcc || (recipients && recipients.bcc)
  const rawTicket = useSelector(state => selectRawTicketById(state, ticketId))
  const [state, dispatch] = useReducer(
    reducer,
    {
      isEditing: isNewTicket,
      rowVisibility: {
        to: true,
        name: false,
        phone: !!phone,
        cc: !!(ccRecipients && ccRecipients.length > 0),
        bcc: !!(bccRecipients && bccRecipients.length > 0),
        subject: isNewTicket || (rawTicket?.subject || '') !== (subject || ''),
      },
      focusField: !toRecipients || toRecipients.length === 0 ? 'to' : null,
      actionsRow: 'to',
    },
    buildInitialState
  )

  const handleExpand = useCallback(
    e => {
      if (e) {
        e.stopPropagation()
      }
      let row
      if (!toRecipients?.length) {
        row = 'to'
      } else if (ccRecipients.length) {
        row = 'cc'
      } else if (bccRecipients.length) {
        row = 'bcc'
      }
      dispatch({ type: 'EXPAND', payload: { row } })
    },
    [dispatch, toRecipients, bccRecipients, ccRecipients]
  )

  const onActionClick = useCallback(
    row => {
      dispatch({ type: 'SHOW_ROW', payload: { row } })
    },
    [dispatch]
  )

  useEffect(
    () => {
      if (isNewTicket) return
      if (isForwardingType) {
        handleExpand()
      }
    },
    [isNewTicket, isForwardingType, handleExpand]
  )

  useLayoutEffect(
    () => {
      if (!isNewTicket) return
      if (toRecipients && toRecipients[0]) {
        const to = toRecipients[0]
        if ((!to.id && !to.name) || to.name === to.email) {
          // HACK (jscheel): Give time for focus to change
          setTimeout(() => {
            dispatch({ type: 'SHOW_ROW', payload: { row: 'name' } })
          }, 1)
        }
      } else {
        dispatch({ type: 'HIDE_ROW', payload: { row: 'name' } })
      }
    },
    [isNewTicket, toRecipients]
  )

  const rowKeyPrefix = [
    ticketId,
    isForwardingType && 'forwarding',
    `${state.focusField}focused`,
    ...Object.keys(state.rowVisibility).map(
      k => (state.rowVisibility[k] ? `${k}visible` : false)
    ),
  ]
    .filter(Boolean)
    .join('-')

  const allRecipients = useMemo(
    () => {
      return toRecipients.concat(ccRecipients, bccRecipients).filter(Boolean)
    },
    [toRecipients, ccRecipients, bccRecipients]
  )

  const actions = useMemo(
    () => {
      return (
        <div className="grui flex actions">
          <Actions
            hideFrom
            hidePhone={!isNewTicket || !isNote || state.rowVisibility.phone}
            hideCc={state.rowVisibility.cc}
            hideBcc={state.rowVisibility.bcc}
            hideSubject={state.rowVisibility.subject}
            onClick={onActionClick}
          />
        </div>
      )
    },
    [state.rowVisibility, isNewTicket, isNote, onActionClick]
  )

  return (
    <div
      className={cn('grui flex flex-col w-100', className)}
      css={styles.gapStyle}
    >
      {state.isEditing && (
        <div css={styles.rowWrapper}>
          <FromRow
            draft={draft}
            mailboxId={mailboxId}
            onMailboxClick={onMailboxClick}
          >
            {children}
          </FromRow>
          {state.actionsRow === 'from' && actions}
        </div>
      )}
      {!state.isEditing && (
        <Recipients recipients={allRecipients} onClickRecipients={handleExpand}>
          {children}
        </Recipients>
      )}
      <div
        className="grui flex flex-col"
        css={[styles.gapStyle, styles.moreOptions]}
      >
        {state.isEditing && (
          <div css={[styles.rowWrapper, children && styles.indentedStyle]}>
            <ToRow
              isForwarding={forwarding}
              ticketId={ticketId}
              recipients={toRecipients}
              recipientsOpenUpward={recipientsOpenUpward}
              startInEditMode={state.focusField === 'to'}
              key={`${rowKeyPrefix}-to`}
              onFocus={propFunc(onActionClick, 'to')}
              tabIndex="0"
              isNote={isNote}
              allowCreateNewContactByName={isNote && isNewTicket}
            />
            {state.actionsRow === 'to' && actions}
          </div>
        )}

        {state.rowVisibility.name &&
          state.isEditing && (
            <div css={[styles.rowWrapper, children && styles.indentedStyle]}>
              <NameRow
                draft={draft}
                draftId={draftId}
                ticketId={ticketId}
                name={name}
                onChange={onNameChange}
                startInEditMode={state.focusField === 'name'}
                key={`${rowKeyPrefix}-name`}
                onFocus={propFunc(onActionClick, 'name')}
                tabIndex="0"
                isNote={isNote}
              />
              {state.actionsRow === 'name' && actions}
            </div>
          )}

        {state.rowVisibility.phone &&
          state.isEditing && (
            <div css={[styles.rowWrapper, children && styles.indentedStyle]}>
              <PhoneRow
                draft={draft}
                draftId={draftId}
                ticketId={ticketId}
                phone={phone}
                onChange={onPhoneChange}
                startInEditMode={state.focusField === 'phone'}
                key={`${rowKeyPrefix}-phone`}
                onFocus={propFunc(onActionClick, 'phone')}
                tabIndex="0"
                isNote={isNote}
              />
              {state.actionsRow === 'phone' && actions}
            </div>
          )}

        {state.rowVisibility.cc &&
          state.isEditing && (
            <div css={[styles.rowWrapper, children && styles.indentedStyle]}>
              <CcRow
                recipients={ccRecipients}
                startInEditMode={state.focusField === 'cc'}
                ticketId={ticketId}
                recipientsOpenUpward={recipientsOpenUpward}
                key={`${rowKeyPrefix}-cc`}
                onFocus={propFunc(onActionClick, 'cc')}
                tabIndex="0"
                isNote={isNote}
              />
              {state.actionsRow === 'c' && actions}
            </div>
          )}

        {state.rowVisibility.bcc &&
          state.isEditing && (
            <div css={[styles.rowWrapper, children && styles.indentedStyle]}>
              <BccRow
                recipients={bccRecipients}
                startInEditMode={state.focusField === 'bcc'}
                ticketId={ticketId}
                recipientsOpenUpward={recipientsOpenUpward}
                key={`${rowKeyPrefix}-bcc`}
                onFocus={propFunc(onActionClick, 'bcc')}
                tabIndex="0"
                isNote={isNote}
              />
              {state.actionsRow === 'bcc' && actions}
            </div>
          )}

        {state.rowVisibility.subject &&
          state.isEditing && (
            <div css={[styles.rowWrapper, children && styles.indentedStyle]}>
              <SubjectRow
                draft={draft}
                draftId={draftId}
                ticketId={ticketId}
                subject={subject}
                onChange={onSubjectChange}
                onEnter={onSubjectEnter}
                startInEditMode={state.focusField === 'subject'}
                key={`${rowKeyPrefix}-subject`}
                onFocus={propFunc(onActionClick, 'subject')}
                tabIndex="0"
              />
              {state.actionsRow === 'subject' && actions}
            </div>
          )}
      </div>
    </div>
  )
}

Expanded.propTypes = {
  recipientsOpenUpward: PropTypes.bool,
}

Expanded.defaultProps = {
  recipientsOpenUpward: false,
}

export default Expanded
